import { CurrencyPipe, DatePipe, KeyValuePipe, NgTemplateOutlet } from '@angular/common'
import { Component, Input, ViewChild } from '@angular/core'
import { NgbNav, NgbNavModule, NgbPopover } from '@ng-bootstrap/ng-bootstrap'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { Organization } from 'src/app/pages/organizations'
import { NgxBootstrapIconsModule } from 'ngx-bootstrap-icons'
import { HeaderComponent } from '../detail-header/detail-header.component'
import { RouterLink } from '@angular/router'
import { AutoFilterTableComponent, FilterableTableColumn } from '../auto-filter-table/auto-filter-table.component'
import { MasterRouterLinkDirective } from '@lib/master-detail'
import { ListItemComponent } from '../list-item/list-item.component'
import { LinkedInComponent } from '../linked-in/linked-in.component'
import { BaseArticleComponent } from '../base-article-component/base-article-component'
import { SECDocumentsViewerComponent } from '../sec-documents-viewer/sec-documents-viewer.component'
import { SearchOptionGroup } from '../filtering-search-component-base/filtering-search-types'
import { SECDocumentSearchGroup } from '../filtering-search-component-base/filtering-search-common-definitions'
import { take } from 'rxjs'
import { organizationFollowOptions } from '../../follow-options'
import { AdobePDFViewerComponent } from '../adobe-pdf-viewer/adobe-pdf-viewer'
import { PDFViewerComponent } from '../pdf-viewer/pdf-viewer.component'
import { SituationTimelineComponent } from '../situation-timeline/situation-timeline.component'
import { PrePostCapitalStructureComponent } from '../prepost-capital-structure/prepost-capital-structure.component'

@Component({
    selector: 'app-organization-overview',
    imports: [
        NgScrollbarModule,
        NgbNavModule,
        NgTemplateOutlet,
        NgbPopover,
        NgxBootstrapIconsModule,
        HeaderComponent,
        RouterLink,
        AutoFilterTableComponent,
        MasterRouterLinkDirective,
        ListItemComponent,
        LinkedInComponent,
        DatePipe,
        KeyValuePipe,
        SECDocumentsViewerComponent,
        CurrencyPipe,
        AdobePDFViewerComponent,
        PDFViewerComponent,
        SituationTimelineComponent,
        PrePostCapitalStructureComponent
    ],
    templateUrl: './organization-overview.component.html',
    styleUrl: './organization-overview.component.scss'
})
export class OrganizationOverviewComponent extends BaseArticleComponent<Organization> {
  @ViewChild('nav') ngbNav: NgbNav | null = null

  @Input() set OrganizationArticle(o: Organization) {
    this.organizationArticle = o
    this.organization = o.body
  }
  @Input() organization: any | undefined
  @Input() addAppHeader: boolean = true
  @Input() creditorMode: boolean = false

  public showAllInCreditorMode = true
  public matchedCreditorCount = 0
  public showMatchedLabels = false
  public creditorFilterButtonText = 'Show All'

  public organizationArticle: Organization | undefined

  public showAllChildren = false
  public showAllStaff = false


  public involvementTableColumns: FilterableTableColumn[] = [
    {
      name: 'situational_date',
      title: 'Situation Date',
      type: 'date',
      sortable: true,
      filterable: false,
      sortby: 'situational_date',
      sorted: 'desc'
    },
    {
      name: 'situation_type',
      title: 'Situation type',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      name: 'actual_entity_name_display',
      title: 'Affiliate/Subsidiary',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'entity_name',
    },
    {
      name: 'case_name_display',
      title: 'Situation name',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'case_name',
    },
    {
      name: 'advisor_display',
      title: 'Advisors',
      type: 'text',
      sortable: false,
      filterable: false
    },
    {
      name: 'related_entity',
      title: 'Client',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'related_entity_name',
    },
    {
      name: 'group_name',
      title: 'Group Name',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'related_entity_name',
    },
    {
      name: 'involvement_role',
      title: 'Role',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      name: 'involvement_type',
      title: 'Type',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      name: 'involvement_subtype',
      title: 'Subtype',
      type: 'text',
      sortable: true,
      filterable: true,
    },
  ]

  public holdingsTableColumns: FilterableTableColumn[] = [
    {
      name: 'holdingDate',
      title: 'Date',
      type: 'date',
      sortable: true,
      filterable: false,
    },
    {
      name: 'case_name_display',
      title: 'Case',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'case_name',
      sorted: 'asc'
    },
    {
      name: 'instrument_name',
      title: 'Instrument',
      type: 'text',
      sortable: true,
      filterable: false,
    },
    {
      name: 'position',
      title: 'Position',
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      name: 'holdingValue',
      title: 'Value',
      type: 'text',
      sortable: true,
      filterable: false,
    },
  ]

  public retentionsTableColumns: FilterableTableColumn[] = [
    {
      name: 'caseLink',
      title: 'Case Name',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'case_name',
      sorted: 'asc'
    },
    {
      name: 'actual_entity_name_display',
      title: 'Affiliate/Subsidiary',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'entity_name',
    },
    {
      name: 'case_filing_date',
      title: 'Case Filing Date',
      type: 'date',
      sortable: true,
      filterable: false,
    },
    {
      name: 'date_retained',
      title: 'Date Retained',
      type: 'date',
      sortable: true,
      filterable: false,
    },
    {
      name: 'order_approved',
      title: 'Order Approved',
      type: 'date',
      sortable: true,
      filterable: false,
    },
    {
      name: 'retained_by',
      title: 'Retained By',
      type: 'text',
      sortable: true,
      filterable: false,
    },
    {
      name: 'involvement_role',
      title: 'Retained Role',
      type: 'text',
      sortable: true,
      filterable: false,
    },
    {
      name: 'date_fees_filed',
      title: 'Date Fees Filed',
      type: 'date',
      sortable: true,
      filterable: false,
    },
    {
      name: 'total_fees',
      title: 'Total Fees',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'total_fees_number',
      sortbyType: 'number',
    },
    {
      name: 'total_expenses',
      title: 'Total Expenses',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'total_expenses_number',
      sortbyType: 'number',
    },
    {
      name: 'total_transaction_fees',
      title: 'Total Transaction Fees',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'total_transaction_fees_number',
      sortbyType: 'number',
    },
  ]

  public linkedInstrumentsTableColumns: FilterableTableColumn[] = [
    {
      name: 'case_name_display',
      title: 'Situation name',
      type: 'text',
      sortable: true,
      filterable: false,
    },
    {
      name: 'attributeName',
      title: 'Type',
      type: 'text',
      sortable: true,
      filterable: true,
    },

    {
      name: 'instrument_name',
      title: 'Instrument',
      type: 'text',
      sortable: true,
      filterable: false,
    },
  ]

  public staffTableColumns: FilterableTableColumn[] = [
    {
      name: 'entity_name_link',
      title: 'Name',
      type: 'text',
      sortable: true,
      filterable: false,
      sortby: 'entity_name',
    },
    {
      name: 'start_involvement',
      title: 'Involvement Date',
      type: 'date',
      sortable: true,
      filterable: false,
      sorted: 'desc'
    },
    {
      name: 'telephone',
      title: 'Telephone',
      type: 'tel',
      sortable: true,
      filterable: false,
    },
    {
      name: 'email',
      title: 'Email',
      type: 'email',
      sortable: true,
      filterable: false,
    },
    {
      name: 'web',
      title: 'Web',
      type: 'web',
      sortable: true,
      filterable: false,
    },
    {
      name: 'linkedin',
      title: 'LinkedIn',
      type: 'linkedin',
      sortable: false,
      filterable: false,
    },
  ]

  public documentSearchOptions: SearchOptionGroup[] = [
    SECDocumentSearchGroup
  ]
  public documentInitialSearch = {}

  public tabs = [
    ['overview', 'Overview'],
    /*
    ['i', 'Income Statement'],
    ['b', 'Balance Sheet'],
    ['c', 'Cash Flow'],
    ['d', 'SEC Disclosure Footnotes'],
    */
    ['documentssec', 'SEC Documents']
  ]

  public activeId = 'overview'

  public followOptions: [string, boolean][] = structuredClone(organizationFollowOptions)

  override ngOnInit() {
    if (!this.organization.summary?.SEC_CIK) {
      this.followOptions.pop()
    }

    if (this.creditorMode) {
      this.followOptions = this.followOptions.map( option => {
        if (option[0] !== 'Trade Exposure') {
          option[1] = false
        }
        return option
      })
      this.matchedCreditorCount = this.organization.creditorpositions.reduce((accumulator:number, current:any) => {
        if (current.matched) {
          return accumulator + 1
        }
        return accumulator
      }, 0)
      this.showMatchedLabels = this.matchedCreditorCount !== this.organization['creditorpositions'].length
      this.toggleShowAllCreditorPosition()
    }

    this.route.fragment.pipe(take(1)).subscribe(fragment => {
      if (fragment) {
        const ngbNvNumber = parseInt(fragment)
        if (ngbNvNumber > 0) {
          setTimeout(() => {
            this.ngbNav?.select('documentssec')
          }, 1000)
        }
      }
    })
  }

  toggleShowAllCreditorPosition() {
    this.showAllInCreditorMode = !this.showAllInCreditorMode
    if (this.showAllInCreditorMode) {
      this.creditorFilterButtonText = `Show Matched (${this.matchedCreditorCount})`
    } else {
      this.creditorFilterButtonText = `Show All (${this.organization.creditorpositions.length})`
    }
  }

  hasLinkedPlans(plansLookup: { [key: string]: any[] }): boolean {
    let result = false
    if (typeof plansLookup == 'object') {
      for (const plans of Object.values(plansLookup)) {
        if (plans.length > 0) {
          result = true
          break
        }
      }
    }
    return result
  }


  getQueryParams(id: number, name: string): any {
    return {
      organization: id + ':' + name
    }
  }

  usableContactInformation(contact: { [key: string]: string }): boolean {
    let result = false
    if (typeof contact == 'object') {
      result = contact['email']?.length > 1 || contact['telephone']?.length > 1 || contact['web']?.length > 1 || contact['linkedin']?.length > 1
    }
    return result
  }

  override sendAnalyticsForTabChange(tabID: string) {
    for (const tab of this.tabs) {
      if (tab[0] == tabID) {
        let tabName = tab[1]
        if (tab[1] != 'Overview') {
          tabName = 'calcbench:' + tabName
        }
        this.mixpanel.trackPageView({
          tabID,
          tab: tabName
        })
        return
      }
    }
  }
}
